import { type ContractChange, type Html, SafeHtml, formatDate } from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { shared } from "../../../styles";

// Only SafeHtml instances carry trusted markup with user input already escaped;
// everything else may contain user input and is rendered as text (lit escapes it).
const renderValue = (value?: Html) =>
    value instanceof SafeHtml ? unsafeHTML(value.value) : value ? String(value) : "";

@customElement("ptc-contract-change")
export class DisplayContractChange extends LitElement {
    @property({ attribute: false })
    change: ContractChange;

    static styles = [
        shared,
        css`
            :host {
                display: block;
            }

            .table {
                display: inline-grid;
                grid-template-columns: auto auto auto;
                grid-gap: 0.5em;
            }

            .line {
                border-right: solid 1px var(--shade-2);
                transform: translate(-50%, 0);
            }
        `,
    ];

    renderTypeIcon(type: "start" | "end" | "pause" | "resume" | "change") {
        const iconStyle = "position: sticky; top: 1em;";
        switch (type) {
            case "start":
                return html`<ptc-ui-icon name="icon.action.start-shift" style="${iconStyle}"></ptc-ui-icon>`;
            case "end":
                return html`<ptc-ui-icon name="icon.action.end-shift" style="${iconStyle}"></ptc-ui-icon>`;
            case "pause":
                return html`<ptc-ui-icon name="icon.status.paused" style="${iconStyle}"></ptc-ui-icon>`;
            case "resume":
                return html`<ptc-ui-icon name="icon.action.start-shift" style="${iconStyle}"></ptc-ui-icon>`;
            case "change":
                return html`<ptc-ui-icon name="icon.status.edited" style="${iconStyle}"></ptc-ui-icon>`;
        }
    }

    render() {
        return html`
            <div class="horizontal layout left-padded">
                <div class="vertical layout">
                    <div class="line" style="height: 1em"></div>
                    ${this.renderTypeIcon(this.change.type)}
                    <div class="line stretch"></div>
                </div>

                <div style="width: 14em; padding: 1em 0.5em; position: sticky; top: 0; align-self: start;">
                    <div>${formatDate(this.change.date)}</div>
                    <div class="bold">
                        ${this.change.type === "change"
                            ? "Vertragsänderung"
                            : this.change.type === "pause"
                              ? "Vertragspause"
                              : this.change.type === "resume"
                                ? "Pause beendet"
                                : this.change.type === "start"
                                  ? "Vertragsbeginn"
                                  : "Vertragsende"}
                    </div>
                    ${this.change.contractB?.comment
                        ? html`
                              <div class="small subtle">
                                  <ptc-ui-icon name="icon.object.comment" class="smaller"></ptc-ui-icon> ${this.change
                                      .contractB.comment}
                              </div>
                          `
                        : ""}
                </div>

                <div class="double-padded spacing stretch grid" style="--grid-column-width: 20em;">
                    ${this.change.type !== "end"
                        ? html`
                              ${this.change.fields.map(
                                  ({ label, before, after }) => html`
                                      <div>
                                          <div class="blue colored-text">${label}:</div>
                                          <div class="center-aligning spacing wrapping horizontal layout relative">
                                              ${["change", "pause", "resume"].includes(this.change.type)
                                                  ? html`
                                                        <div class="fit-horizontally">${renderValue(before)}</div>
                                                        <div class="bold">
                                                            <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                                                        </div>
                                                    `
                                                  : ""}
                                              ${after
                                                  ? html` <div class="fit-horizontally">${renderValue(after)}</div> `
                                                  : ""}
                                          </div>
                                      </div>
                                  `
                              )}
                          `
                        : ""}
                </div>
            </div>
        `;
    }
}
